import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { useAuth } from "../../../app/providers/AuthProvider";
import { profileService } from "../../../services/profile";
import { formatApiError } from "../../../services/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

export function ChangePasswordSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clearSession } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const e = {};
    if (!current) e.current = t("auth.password_required");
    if (next.length < 8) e.next = t("profile.password_too_short");
    else if (!/\d/.test(next)) e.next = t("profile.password_needs_digit");
    if (next !== confirm) e.confirm = t("profile.password_mismatch");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || submitting) return;
    setSubmitting(true);
    try {
      await profileService.changePassword(current, next);
      clearSession();
      toast.success(t("auth.password_changed_relogin"));
      navigate("/login", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const message =
        status === 400
          ? t("profile.password_wrong_current")
          : formatApiError(err, t("common.error_generic"));
      if (status === 400) {
        setErrors((prev) => ({ ...prev, current: message }));
      } else {
        toast.error(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-lg border border-border-subtle bg-surface p-6 shadow-card"
      data-testid="change-password-section"
    >
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-text-primary">
          {t("profile.change_password_heading")}
        </h2>
        <div className="h-px w-8 bg-champagne" aria-hidden />
      </div>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="cp-current">{t("profile.current_password")}</Label>
          <Input
            id="cp-current"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            data-testid="cp-current"
            className="h-10 bg-surface border-border-subtle focus-visible:ring-champagne"
          />
          {errors.current && (
            <p
              className="text-xs text-status-unavailable"
              data-testid="cp-current-error"
            >
              {errors.current}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="cp-next">{t("profile.new_password")}</Label>
          <Input
            id="cp-next"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            data-testid="cp-next"
            className="h-10 bg-surface border-border-subtle focus-visible:ring-champagne"
          />
          {errors.next ? (
            <p
              className="text-xs text-status-unavailable"
              data-testid="cp-next-error"
            >
              {errors.next}
            </p>
          ) : (
            <p className="text-xs text-text-secondary">
              {t("profile.password_rules")}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="cp-confirm">{t("profile.confirm_password")}</Label>
          <Input
            id="cp-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="cp-confirm"
            className="h-10 bg-surface border-border-subtle focus-visible:ring-champagne"
          />
          {errors.confirm && (
            <p
              className="text-xs text-status-unavailable"
              data-testid="cp-confirm-error"
            >
              {errors.confirm}
            </p>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          type="submit"
          disabled={submitting}
          data-testid="cp-submit"
          className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("common.saving")}
            </>
          ) : (
            t("profile.change_password_submit")
          )}
        </Button>
      </div>
    </form>
  );
}
